import { getApiBaseUrl } from "@/lib/api-base";
import { editorHeaders } from "@/lib/sacred-library/sacred-library-api";
import { getChantSessionId, setChantSessionId } from "@/lib/chanting/chanting-service";

const API = getApiBaseUrl();

async function getJson(path, options = {}) {
  const res = await fetch(`${API}${path}`, options);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let detail = text || `Townhall API ${path}: ${res.status}`;
    try {
      const parsed = JSON.parse(text);
      detail = parsed.detail || parsed.error || detail;
    } catch {
      /* keep raw */
    }
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** Resolve deity/image paths (R2 Worker assets, D1 Townhall asset URLs, or static). */
export function resolveTownhallImageSrc(src) {
  if (!src) return null;
  if (src.startsWith("http") || src.startsWith("data:") || src.startsWith("blob:")) return src;
  // Same-origin /api/* works when Pages proxies to Worker; prefix API base in local CRA.
  if (src.startsWith("/api/assets/") || src.startsWith("/api/townhall/")) {
    if (API && !API.startsWith("/") && typeof window !== "undefined") {
      // Absolute Worker URL in .env.development.local
      if (API.includes("://")) return `${API.replace(/\/$/, "")}${src.replace(/^\/api/, "")}`;
    }
    return src;
  }
  if (src.startsWith("/assets/")) return `${API}/assets/${src.slice("/assets/".length)}`;
  if (src.startsWith("/townhall/asset/")) return `${API}${src}`;
  if (src.startsWith("/sacred-library/")) return `${API}${src}`;
  if (src.startsWith("/api/")) return src;
  return src;
}

/** D1-backed Townhall deity asset (not KV). */
export function townhallUploadedAssetSrc(chantId, version) {
  if (!chantId) return null;
  const base = `${API}/townhall/asset/${encodeURIComponent(chantId)}`;
  return version ? `${base}?v=${encodeURIComponent(version)}` : `${base}?v=${Date.now()}`;
}

export function fetchTownhallHub() {
  return getJson("/townhall");
}

export function fetchTownhallInspiration() {
  return getJson("/townhall/inspiration");
}

export function fetchTownhallChant(chantId, { lang = "en", sessionId } = {}) {
  const q = new URLSearchParams({ lang });
  const sid = sessionId || getChantSessionId();
  if (sid) q.set("session_id", sid);
  return getJson(`/townhall/${encodeURIComponent(chantId)}?${q}`);
}

export function postOfflineCount({ chantId, count, sessionId, token }) {
  const headers = { "Content-Type": "application/json", ...editorHeaders() };
  if (token) headers.Authorization = `Bearer ${token}`;
  const sid = sessionId || getChantSessionId();
  return getJson("/townhall/offline-count", {
    method: "POST",
    headers,
    body: JSON.stringify({
      chant_id: chantId,
      count,
      session_id: sid || undefined,
    }),
  }).then((result) => {
    if (result?.session?.session_id) setChantSessionId(result.session.session_id);
    return result;
  });
}

export function postTownhallPresence({ chantId, sessionId }) {
  if (!chantId || !sessionId) return Promise.resolve();
  return getJson("/townhall/presence", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chant_id: chantId, session_id: sessionId }),
  }).catch(() => {});
}

export function fetchTownhallAdmin(chantId) {
  return getJson(`/townhall/admin/${encodeURIComponent(chantId)}`, {
    headers: editorHeaders(),
  });
}

export function saveTownhallAdmin(chantId, patch) {
  return getJson(`/townhall/admin/${encodeURIComponent(chantId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...editorHeaders() },
    body: JSON.stringify(patch),
  });
}

/** Upload deity image into D1 (townhall_assets) — KV-free. */
export async function uploadTownhallDeityImage(chantId, dataBase64, mime) {
  const res = await getJson(`/townhall/admin/${encodeURIComponent(chantId)}/image`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...editorHeaders() },
    body: JSON.stringify({ data_base64: dataBase64, mime }),
  });
  return {
    ...res,
    image_url: res.image_url,
    display_url: resolveTownhallImageSrc(res.image_url) || townhallUploadedAssetSrc(chantId),
  };
}

export function subscribeTownhallInspiration(onUpdate, intervalMs = 15_000) {
  let active = true;
  const poll = async () => {
    if (!active) return;
    try {
      onUpdate(await fetchTownhallInspiration());
    } catch {
      /* retry */
    }
  };
  poll();
  const id = setInterval(poll, intervalMs);
  return () => {
    active = false;
    clearInterval(id);
  };
}

/**
 * Preferred Prayer Hall / Townhall sloka fetch — D1 Townhall API.
 * Returns slides shaped as { sanskrit, transliteration: { en, te, hi }, ... }.
 * Does not rely on KV or static JSON when D1 has content.
 */
export async function fetchSlokas(chantId, { lang = "en", signal } = {}) {
  const id = String(chantId || "").trim();
  if (!id) return [];
  try {
    const q = new URLSearchParams({ chant_id: id, lang: String(lang || "en") });
    const res = await fetch(`${API}/slokas?${q}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.slokas)
        ? data.slokas
        : Array.isArray(data?.slides)
          ? data.slides
          : Array.isArray(data)
            ? data
            : [];
      if (list.length) return list;
    }
  } catch {
    /* fall through */
  }
  try {
    const payload = await fetchTownhallChant(id, { lang });
    return Array.isArray(payload?.slides) ? payload.slides : [];
  } catch {
    return [];
  }
}

export async function fetchTownhallSlides(chantId, { lang = "en", signal } = {}) {
  return fetchSlokas(chantId, { lang, signal });
}
